<template>
  <div class="premium-input-wrap" :class="{ 'is-dark-wrap': isDark }">
    <!-- Label -->
    <label v-if="label" class="premium-label">{{ label }}</label>

    <!-- ── TEXTAREA (multiline / resizable) ── -->
    <v-textarea
      v-if="multiline"
      v-model="internalValue"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      outlined
      dense
      hide-details="auto"
      :rows="rows"
      :auto-grow="autoGrow"
      :no-resize="!resizable"
      class="premium-input premium-textarea"
      :class="{ 'is-dark': isDark }"
      :dark="isDark"
      v-bind="$attrs"
      :error-messages="error"
      :error="!!error"
      v-on="listenersWithoutInput"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-textarea>

    <!-- ── AUTOCOMPLETE FIELD ── -->
    <v-autocomplete
      v-else-if="isSelect && searchable"
      v-model="internalValue"
      :items="items"
      :item-text="itemText"
      :item-value="itemValue"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      outlined
      dense
      hide-details="auto"
      class="premium-input"
      :class="{ 'is-dark': isDark }"
      :dark="isDark"
      v-bind="$attrs"
      :error-messages="error"
      :error="!!error"
      v-on="listenersWithoutInput"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-autocomplete>

    <!-- ── SELECT FIELD ── -->
    <v-select
      v-else-if="isSelect"
      v-model="internalValue"
      :items="items"
      :item-text="itemText"
      :item-value="itemValue"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      outlined
      dense
      hide-details="auto"
      class="premium-input"
      :class="{ 'is-dark': isDark }"
      :dark="isDark"
      v-bind="$attrs"
      :error-messages="error"
      :error="!!error"
      v-on="listenersWithoutInput"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-select>

    <!-- ── TEXT FIELD (default) ── -->
    <v-text-field
      v-else
      v-model="internalValue"
      :type="inputType === 'password' && !showPassword ? 'password' : (inputType === 'password' ? 'text' : inputType)"
      :placeholder="placeholder"
      :prepend-inner-icon="icon"
      :append-icon="inputType === 'password' ? (showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline') : appendIcon"
      outlined
      dense
      hide-details="auto"
      class="premium-input"
      :class="{ 'is-dark': isDark }"
      :dark="isDark"
      v-bind="$attrs"
      :error-messages="error"
      :error="!!error"
      @click:append="inputType === 'password' ? showPassword = !showPassword : null"
      @wheel="handleWheel"
      v-on="listenersWithoutInput"
    >
      <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
        <slot :name="slotName" />
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'PremiumInput',
  inheritAttrs: false,

  props: {
    value:       { default: '' },
    label:       { type: String,  default: '' },
    placeholder: { type: String,  default: '' },
    type:        { type: String,  default: 'text' },
    icon:        { type: String,  default: '' },
    appendIcon:  { type: String,  default: '' },
    error:       { type: [String, Array], default: '' },

    // ── new textarea props ──
    multiline:   { type: Boolean, default: false }, // switch to textarea
    rows:        { type: [Number, String], default: 2 }, // initial row height
    autoGrow:    { type: Boolean, default: true  }, // grow with content
    resizable:   { type: Boolean, default: true  }, // show resize handle

    // ── select props ──
    isSelect:    { type: Boolean, default: false },
    searchable:  { type: Boolean, default: false },
    items:       { type: Array,   default: () => [] },
    itemText:    { type: String,  default: 'text' },
    itemValue:   { type: String,  default: 'value' },
  },

  data() {
    return {
      showPassword: false,
      inputType: this.type,
    };
  },

  computed: {
    ...mapState('theme', ['isDark', 'primaryColor']),

    internalValue: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },

    /**
     * Exclude 'input' from $listeners so that internalValue's setter is the
     * sole handler for the input event. Passing 'input' via v-on="$listeners"
     * AND having internalValue emit 'input' causes a double-emit that can
     * reset the field value (the parent's raw DOM event handler fires with
     * the Event object instead of the typed string).
     */
    listenersWithoutInput() {
      const listeners = { ...this.$listeners };
      delete listeners.input;
      return listeners;
    },
  },

  methods: {
    handleWheel(e) {
      if (this.inputType === 'number') {
        if (e.target && typeof e.target.blur === 'function') {
          e.target.blur();
        } else if (document.activeElement && document.activeElement.type === 'number') {
          document.activeElement.blur();
        }
      }
    }
  },

  watch: {
    type(newType) { this.inputType = newType; },
  },
};
</script>

<style scoped>
.premium-input-wrap {
  margin-bottom: 2px;
}

.premium-label {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #6B7280);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 4px;
}

/* ── shared fieldset / border ── */
.premium-input >>> fieldset {
  display: none !important;
}

.premium-input >>> .v-input__slot {
  border: 1px solid var(--border, rgba(0,0,0,0.1)) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
  overflow: hidden !important;
}

.premium-input.v-input--is-focused >>> .v-input__slot {
  border-color: var(--brand, #10B981) !important;
  border-width: 1.5px !important;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.12) !important;
  background: #ffffff !important;
}

/* ── text-field height ── */
.premium-input:not(.premium-textarea) >>> .v-input__control {
  min-height: 46px !important;
}

/* ── shared text style ── */
.premium-input >>> input,
.premium-input >>> textarea {
  font-size: 13.5px !important;
  font-weight: 500 !important;
  color: var(--tx, #111827) !important;
}

/* ── textarea: user-resizable via drag handle ── */
.premium-textarea >>> textarea {
  resize: vertical !important;
  min-height: 46px !important;
  line-height: 1.6 !important;
  padding-top: 10px !important;
  padding-bottom: 8px !important;
}

/* ── dark mode ── */
.is-dark-wrap .premium-label,
.theme--dark .premium-label,
[data-theme="dark"] .premium-label {
  color: #E5E5E5 !important;
}

.is-dark-wrap >>> .v-input__slot input,
.is-dark-wrap >>> .v-input__slot textarea,
.is-dark-wrap >>> input,
.is-dark-wrap >>> textarea,
.theme--dark.premium-input >>> .v-input__slot input,
.theme--dark.premium-input >>> .v-input__slot textarea,
.theme--dark.premium-input >>> input,
.theme--dark.premium-input >>> textarea {
  color: #FFFFFF !important;
}

.is-dark-wrap >>> input::placeholder,
.is-dark-wrap >>> textarea::placeholder,
.is-dark-wrap >>> .v-text-field__slot input::placeholder,
.is-dark-wrap >>> .v-textarea__slot textarea::placeholder,
.theme--dark.premium-input >>> input::placeholder,
.theme--dark.premium-input >>> textarea::placeholder,
.theme--dark.premium-input >>> .v-text-field__slot input::placeholder,
.theme--dark.premium-input >>> .v-textarea__slot textarea::placeholder {
  color: #A3A3A3 !important;
  opacity: 1 !important;
}

.is-dark-wrap >>> .v-input__slot,
.theme--dark.premium-input >>> .v-input__slot {
  background: #1e293b !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

/* Fix Vuetify internal slot background in dark mode */
.is-dark-wrap >>> .v-text-field__slot,
.is-dark-wrap >>> .v-select__slot,
.theme--dark.premium-input >>> .v-text-field__slot,
.theme--dark.premium-input >>> .v-select__slot {
  background: #1e293b !important;
}

/* Fix icon area background bleeding */
.is-dark-wrap >>> .v-input__prepend-inner,
.is-dark-wrap >>> .v-input__append-inner,
.theme--dark.premium-input >>> .v-input__prepend-inner,
.theme--dark.premium-input >>> .v-input__append-inner {
  background: #1e293b !important;
}

.is-dark-wrap >>> .v-input__slot:hover,
.theme--dark.premium-input >>> .v-input__slot:hover {
  border-color: rgba(255, 255, 255, 0.25) !important;
}

/* ── dark mode: focused state — keep dark bg, don't let Vuetify flash white ── */
.is-dark-wrap >>> .v-input--is-focused .v-input__slot,
.theme--dark.premium-input.v-input--is-focused >>> .v-input__slot {
  border-color: var(--brand, #E5E5E5) !important;
  background: #273548 !important;
}

.is-dark-wrap >>> .v-input--is-focused .v-input__prepend-inner,
.is-dark-wrap >>> .v-input--is-focused .v-input__append-inner,
.theme--dark.premium-input.v-input--is-focused >>> .v-input__prepend-inner,
.theme--dark.premium-input.v-input--is-focused >>> .v-input__append-inner {
  background: #273548 !important;
}

.is-dark-wrap >>> .v-input--is-focused .v-text-field__slot,
.is-dark-wrap >>> .v-input--is-focused .v-select__slot,
.theme--dark.premium-input.v-input--is-focused >>> .v-text-field__slot,
.theme--dark.premium-input.v-input--is-focused >>> .v-select__slot {
  background: #273548 !important;
}

/* ── icon ── */
.premium-input >>> .v-icon {
  font-size: 18px !important;
  color: #A3A3A3 !important;
  transition: color 0.2s ease;
}

.is-dark-wrap >>> .v-icon {
  color: #A3A3A3 !important;
}

.premium-input.v-input--is-focused >>> .v-icon {
  color: var(--brand, #10B981) !important;
}

/* ── icon alignment when inside a textarea ── */
.premium-textarea >>> .v-input__prepend-inner {
  margin-top: 12px !important;
}

/* ── error state ── */
.premium-input.v-input--has-state.error--text >>> .v-input__slot {
  border-color: #EF4444 !important;
}
</style>
